import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { showSuccess } from '../../utils/toast';
import BottomNav from '../../components/layouts/BottomNav';
import { FaUserCircle } from 'react-icons/fa';
import venzoLogo from '../../assets/Venzo-Logo.png.webp';
import { getLoggedInUser } from '../../utils/auth';

// Local images for slider
import sliderImg1 from '../../assets/DSC6391.webp';
import sliderImg2 from '../../assets/DSC6393.webp';
import sliderImg3 from '../../assets/DSC_5423-1024x681.webp';

// Import PDF files for downloads (fallback)
import artePdf from '../../assets/downlaods/arte.pdf';
import artistryPdf from '../../assets/downlaods/Artistry.pdf';
import flamedPdf from '../../assets/downlaods/Flamed.pdf';
import organicPdf from '../../assets/downlaods/Organic.pdf';
import etchPdf from '../../assets/downlaods/Etch.pdf';
import impressPdf from '../../assets/downlaods/Impress.pdf';
import sculptPdf from '../../assets/downlaods/Sculpt.pdf';
import hybridZip from '../../assets/downlaods/Hybrid 1 2 3.zip';

import { API_BASE } from '../../utils/url';
const DEFAULT_AVATAR = "https://randomuser.me/api/portraits/men/1.jpg";

// Slider data with local images
const sliderData = [
  {
    image: sliderImg1,
    title: "Focus And\nCreativity",
    button: "KNOW MORE",
    link: "#"
  },
  {
    image: sliderImg2,
    title: "Inspired By Nature",
    button: "KNOW MORE",
    link: "#"
  },
  {
    image: sliderImg3,
    title: "Natural Beauty",
    button: "KNOW MORE",
    link: "#"
  }
];

// Fallback E-catalogue data (if API fails)
const fallbackCatalogueData = [
  {
    id: 1,
    name: "Arte",
    file: artePdf,
    type: "PDF",
    size: "8.3MB"
  },
  {
    id: 2,
    name: "Artistry",
    file: artistryPdf,
    type: "PDF",
    size: "15MB"
  },
  {
    id: 3,
    name: "Flamed",
    file: flamedPdf,
    type: "PDF",
    size: "26MB"
  },
  {
    id: 4,
    name: "Organic",
    file: organicPdf,
    type: "PDF",
    size: "20MB"
  },
  {
    id: 5,
    name: "Etch",
    file: etchPdf,
    type: "PDF",
    size: "37MB"
  },
  {
    id: 6,
    name: "Impress",
    file: impressPdf,
    type: "PDF",
    size: "4.2MB"
  },
  {
    id: 7,
    name: "Sculpt",
    file: sculptPdf,
    type: "PDF",
    size: "48MB"
  },
  {
    id: 8,
    name: "Hybrid 1 2 3",
    file: hybridZip,
    type: "ZIP",
    size: "62MB"
  }
];

const DashboardPage = () => {
  const navigate = useNavigate();

  // --- Get user info ---
  const [user, setUser] = useState(() => {
    const u = getLoggedInUser();
    return {
      name: u?.name || 'Guest',
      avatar: u?.profile_photo
        ? (u.profile_photo.startsWith('http')
          ? u.profile_photo
          : API_BASE + u.profile_photo.replace(/^\/+/, ''))
        : DEFAULT_AVATAR
    };
  });

  const [greeting, setGreeting] = useState('');
  const [stories, setStories] = useState([]);
  const [popupStory, setPopupStory] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [collections, setCollections] = useState([]);
  const [catalogues, setCatalogues] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [heroSliders, setHeroSliders] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const ensureSession = async () => {
      const token = localStorage.getItem('token');
      const loggedInUser = getLoggedInUser();

      console.log('Dashboard Auth Check:', { token: !!token, user: !!loggedInUser });

      if (!token) {
        window.location.replace('/login');
        return;
      }

      // If token exists but cached user is missing/corrupt, recover user from backend.
      if (!loggedInUser || !loggedInUser.id) {
        try {
          const { data } = await axios.get('/users/me');
          if (!isMounted) return;

          localStorage.setItem('user', JSON.stringify(data));
          setUser({
            name: data?.name || 'Guest',
            avatar: data?.profile_photo
              ? (data.profile_photo.startsWith('http')
                ? data.profile_photo
                : API_BASE + data.profile_photo.replace(/^\/+/, ''))
              : DEFAULT_AVATAR
          });
        } catch (error) {
          console.error('Dashboard: failed to recover user from token', error);

          if (error?.response && (error.response.status === 401 || error.response.status === 403)) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('session_owner');
          }
          if (isMounted) {
            window.location.replace('/login');
          }
          return;
        }
      }

      const hour = new Date().getHours();
      let greet = 'Good Morning';
      if (hour >= 12 && hour < 17) greet = 'Good Afternoon';
      if (hour >= 17) greet = 'Good Evening';
      if (isMounted) {
        setGreeting(greet);
      }
    };

    ensureSession();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    axios.get('/stories')
      .then(res => setStories(res.data))
      .catch(() => setStories([]));
  }, []);

  useEffect(() => {
    axios.get('/categories')
      .then(res => setCollections(res.data))
      .catch(() => setCollections([]));
  }, []);

  useEffect(() => {
    axios.get('/e-catalogues')
      .then(res => setCatalogues(res.data.filter(cat => cat.isActive)))
      .catch(() => setCatalogues(fallbackCatalogueData));
  }, []);

  useEffect(() => {
    axios.get('/sliders/active')
      .then(res => setSliders(res.data))
      .catch(() => setSliders([]));
  }, []);

  useEffect(() => {
    axios.get('/hero-sliders/active')
      .then(res => setHeroSliders(res.data))
      .catch(() => setHeroSliders([]));
  }, []);

  useEffect(() => {
    axios.get('/events/active')
      .then(res => setEvents(res.data))
      .catch(() => setEvents([]));
  }, []);

  // Story Auto-Advance Timer
  useEffect(() => {
    let timer;
    if (popupStory) {
      timer = setTimeout(() => {
        const currentIndex = stories.findIndex(s => s.id === popupStory.id);
        if (currentIndex !== -1 && currentIndex < stories.length - 1) {
          setPopupStory(stories[currentIndex + 1]);
        } else {
          setPopupStory(null); // Close if last story
        }
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [popupStory, stories]);

  // Auto-slide functionality - changes slide every 3 seconds
  useEffect(() => {
    const activeSlides = heroSliders.length > 0 ? heroSliders : sliderData;
    if (activeSlides.length <= 1) return; // Don't auto-slide if only one slide

    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => {
        return (prevSlide + 1) % activeSlides.length;
      });
    }, 3000); // 3 seconds

    return () => clearInterval(interval);
  }, [heroSliders.length, sliderData.length]);

  // Update user info from localStorage if it changes (e.g., after profile update)
  useEffect(() => {
    const updateUser = () => {
      const u = getLoggedInUser();
      setUser({
        name: u?.name || 'Guest',
        avatar: u?.profile_photo
          ? (u.profile_photo.startsWith('http')
            ? u.profile_photo
            : API_BASE + u.profile_photo.replace(/^\/+/, ''))
          : DEFAULT_AVATAR
      });
    };
    updateUser();
    window.addEventListener('focus', updateUser);
    return () => window.removeEventListener('focus', updateUser);
  }, []);

  const handleDotClick = idx => setCurrentSlide(idx);

  const handleDownload = async (file, fileName) => {
    try {
      // Show loading state
      const loadingToast = Swal.fire({
        title: 'Downloading...',
        text: `Preparing ${fileName} for download`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Create download link
      const link = document.createElement('a');
      link.href = file;
      link.download = fileName;
      link.target = '_blank';

      // Add to DOM and trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Close loading and show success
      loadingToast.close();
      Swal.fire({
        icon: 'success',
        title: 'Download Started!',
        text: `${fileName} is being downloaded`,
        timer: 2000,
        showConfirmButton: false,
        background: '#f8f9fa',
        color: '#1a1a1a'
      });

    } catch (error) {
      console.error('Download error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Download Failed',
        text: 'Unable to download the file. Please try again.',
        confirmButtonColor: '#1a1a1a'
      });
    }
  };



  return (
    <div style={{
      padding: 18, background: '#fff', minHeight: '100vh',
      fontFamily: 'Montserrat, Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <img
          src={user.avatar}
          alt={user.name}
          style={{
            width: 46, height: 46,
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: 12,
            border: '2.5px solid #d6d6d6',
            background: "#f3f3f3"
          }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: '#222' }}>
            Hi <span role="img" aria-label="wave">👋</span>, {user.name}
          </div>
          <div style={{ fontSize: 14, color: '#8C8C8C', fontWeight: 500 }}>{greeting}</div>
        </div>
        {/* User icon (top right) */}
        <button
          style={{
            background: 'none',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => navigate('/account')}
          aria-label="Account"
        >
          <FaUserCircle size={30} color="#232323" />
        </button>
      </div>

      {/* Stories */}
      <div style={{
        display: 'flex', overflowX: 'auto', gap: 4,
        paddingBottom: 8, marginLeft: -4, marginRight: -4,
      }}>
        {stories.map(story => (
          <div key={story.id} style={{ textAlign: 'center', minWidth: 80 }}>
            <div
              style={{
                width: 68,
                height: 68,
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto',
                cursor: 'pointer'
              }}
              onClick={() => setPopupStory(story)}
            >
              <img
                src={
                  story.image_path?.startsWith('http')
                    ? story.image_path
                    : API_BASE + (story.image_path || '').replace(/^\/+/, '')
                }
                alt={story.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{
              fontSize: 13,
              marginTop: 7,
              color: '#222',
              fontWeight: 500,
              letterSpacing: '.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 80
            }}>{story.title}</div>
          </div>
        ))}
      </div>

      {/* Slider Section */}
      <div style={{
        width: '100%',
        margin: '0 auto',
        position: 'relative',
        borderRadius: 22,
        overflow: 'visible',
        marginTop: 28,
        marginBottom: 36,
        boxShadow: "0 6px 24px 0 rgba(20,20,30,0.09)",
        background: '#fff'
      }}>
        <div style={{
          borderRadius: 22,
          overflow: 'hidden',
          height: 340,
          position: 'relative',
          boxShadow: "0 2px 12px 0 rgba(20,20,30,0.08)"
        }}>
          {heroSliders.length > 0 ? heroSliders.map((slide, i) => (
            <div
              key={slide.id}
              style={{
                display: i === currentSlide ? 'block' : 'none',
                width: '100%',
                height: 340,
                position: 'relative',
              }}
            >
              <img
                src={slide.image_path.startsWith('http') ? slide.image_path : `${API_BASE}${slide.image_path}`}
                alt={slide.title}
                style={{
                  width: '100%',
                  height: 340,
                  objectFit: 'cover',
                  borderRadius: 22,
                  boxShadow: "0 6px 30px 0 rgba(30,30,35,0.07)"
                }}
              />
              <div style={{
                position: 'absolute',
                left: 0, bottom: 44, width: '100%',
                color: '#fff',
                padding: '0 26px',
                textShadow: '0 3px 18px rgba(0,0,0,0.25)'
              }}>
                <div style={{
                  fontWeight: 700,
                  fontSize: 30,
                  marginBottom: 10,
                  whiteSpace: 'pre-line',
                  lineHeight: '1.1'
                }}>
                  {slide.title}
                </div>
                <span
                  onClick={() => navigate(slide.button_link || '/categories')}
                  style={{
                    fontWeight: 600,
                    fontSize: 15,
                    color: '#fff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    display: 'inline-block',
                    letterSpacing: '.5px',
                    opacity: 0.95,
                    marginTop: 5,
                    paddingBottom: 2,
                  }}
                >
                  {slide.button_text || 'KNOW MORE'}
                </span>
              </div>
            </div>
          )) : sliderData.map((slide, i) => (
            <div
              key={i}
              style={{
                display: i === currentSlide ? 'block' : 'none',
                width: '100%',
                height: 340,
                position: 'relative',
              }}
            >
              <img
                src={slide.image}
                alt={slide.title}
                style={{
                  width: '100%',
                  height: 340,
                  objectFit: 'cover',
                  borderRadius: 22,
                  boxShadow: "0 6px 30px 0 rgba(30,30,35,0.07)"
                }}
              />
              <div style={{
                position: 'absolute',
                left: 0, bottom: 44, width: '100%',
                color: '#fff',
                padding: '0 26px',
                textShadow: '0 3px 18px rgba(0,0,0,0.25)'
              }}>
                <div style={{
                  fontWeight: 700,
                  fontSize: 30,
                  marginBottom: 10,
                  whiteSpace: 'pre-line',
                  lineHeight: '1.1'
                }}>
                  {slide.title}
                </div>
                <span
                  onClick={() => navigate('/categories')}
                  style={{
                    fontWeight: 600,
                    fontSize: 15,
                    color: '#fff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    display: 'inline-block',
                    letterSpacing: '.5px',
                    opacity: 0.95,
                    marginTop: 5,
                    paddingBottom: 2,
                  }}
                >
                  KNOW MORE
                </span>
              </div>
            </div>
          ))}
          {/* Slider Dots */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 20,
            display: 'flex',
            gap: 10
          }}>
            {(heroSliders.length > 0 ? heroSliders : sliderData).map((_, idx) => (
              <span
                key={idx}
                onClick={() => handleDotClick(idx)}
                style={{
                  width: 13, height: 13,
                  borderRadius: '50%',
                  background: currentSlide === idx ? '#fff' : 'rgba(255,255,255,0.42)',
                  border: currentSlide === idx ? '2px solid #233045' : '2px solid #eee',
                  display: 'inline-block',
                  cursor: 'pointer',
                  boxShadow: currentSlide === idx ? '0 2px 8px rgba(10,20,40,0.13)' : undefined,
                  marginTop: 4
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Collections Section */}
      <div style={{
        marginTop: 12,
        marginBottom: 24,
        paddingLeft: 2,
        paddingRight: 2
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10
        }}>
          <div style={{
            fontSize: 20,
            fontWeight: 600,
            color: '#233045'
          }}>Collections</div>
          <button
            style={{
              background: 'none',
              border: 'none',
              color: '#233045',
              fontWeight: 500,
              fontSize: 15,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            onClick={() => navigate('/categories')}
          >
            view all
          </button>
        </div>
        <div style={{
          display: 'flex',
          overflowX: 'auto',
          gap: 16,
          paddingBottom: 4
        }}>
          {collections.map((cat) => (
            <div
              key={cat.id}
              onClick={() => navigate(`/products?category=${cat.id}`)}
              style={{
                minWidth: 148,
                maxWidth: 148,
                flex: '0 0 148px',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
                boxShadow: '0 0 0 rgba(0,0,0,0.05)',
              }}
            >
              <div style={{
                width: 148,
                height: 170,
                borderRadius: 12,
                overflow: 'hidden',
                marginBottom: 7,
                background: '#f2f2f2',
                transition: 'box-shadow 0.18s'
              }}>
                <img
                  src={
                    cat.image_path?.startsWith('http')
                      ? cat.image_path
                      : API_BASE + cat.image_path
                  }
                  alt={cat.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              <div style={{
                fontSize: 15,
                color: '#223',
                fontWeight: 500,
                textAlign: 'left',
                paddingLeft: 2,
                paddingRight: 2,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}>{cat.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Slider Section */}
      {sliders.length > 0 && (
        <div style={{
          marginTop: 12,
          marginBottom: 24,
          paddingLeft: 2,
          paddingRight: 2
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10
          }}>
            <div style={{
              fontSize: 20,
              fontWeight: 600,
              color: '#233045'
            }}>Featured</div>
          </div>

          <div style={{
            width: '100%',
            margin: '0 auto',
            position: 'relative',
            borderRadius: 16,
            overflow: 'hidden',
            height: 230,
            boxShadow: "0 4px 20px 0 rgba(20,20,30,0.08)",
            background: '#fff'
          }}>
            <div style={{
              borderRadius: 16,
              overflow: 'hidden',
              height: 230,
              position: 'relative',
              boxShadow: "0 2px 12px 0 rgba(20,20,30,0.06)"
            }}>
              {sliders.map((slide, i) => (
                <div
                  key={slide.id}
                  style={{
                    display: i === currentSlide ? 'block' : 'none',
                    width: '100%',
                    height: 230,
                    position: 'relative',
                  }}
                >
                  <img
                    src={slide.image_path.startsWith('http') ? slide.image_path : `${API_BASE}${slide.image_path}`}
                    alt={slide.title}
                    style={{
                      width: '100%',
                      height: 230,
                      objectFit: 'cover',
                      borderRadius: 16,
                      boxShadow: "0 4px 20px 0 rgba(30,30,35,0.07)"
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    left: 0, bottom: 30, width: '100%',
                    color: '#fff',
                    padding: '0 20px',
                    textShadow: '0 2px 12px rgba(0,0,0,0.3)'
                  }}>
                    <div style={{
                      fontWeight: 700,
                      fontSize: 24,
                      marginBottom: 8,
                      whiteSpace: 'pre-line',
                      lineHeight: '1.1'
                    }}>
                      {slide.title}
                    </div>
                    {slide.subtitle && (
                      <div style={{
                        fontWeight: 500,
                        fontSize: 14,
                        marginBottom: 8,
                        opacity: 0.9
                      }}>
                        {slide.subtitle}
                      </div>
                    )}
                    <span
                      onClick={() => navigate(slide.button_link || '/categories')}
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: '#fff',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        display: 'inline-block',
                        letterSpacing: '.5px',
                        opacity: 0.95,
                        marginTop: 4,
                        paddingBottom: 2,
                      }}
                    >
                      {slide.button_text || 'KNOW MORE'}
                    </span>
                  </div>
                </div>
              ))}
              {/* Slider Dots */}
              {sliders.length > 1 && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bottom: 15,
                  display: 'flex',
                  gap: 8
                }}>
                  {sliders.map((_, idx) => (
                    <span
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      style={{
                        width: 10, height: 10,
                        borderRadius: '50%',
                        background: currentSlide === idx ? '#fff' : 'rgba(255,255,255,0.4)',
                        border: currentSlide === idx ? '2px solid #233045' : '2px solid #eee',
                        display: 'inline-block',
                        cursor: 'pointer',
                        boxShadow: currentSlide === idx ? '0 2px 6px rgba(10,20,40,0.15)' : undefined,
                        marginTop: 2
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Future Events and Exhibitions Section */}
      <div style={{
        marginTop: 12,
        marginBottom: 24,
        paddingLeft: 2,
        paddingRight: 2
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10
        }}>
          <div style={{
            fontSize: 20,
            fontWeight: 600,
            color: '#233045'
          }}>Future Events & Exhibitions</div>
        </div>

        {/* Events Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 16,
          paddingBottom: 4
        }}>
          {events.map((event) => (
            <div
              key={event.id}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                border: '1px solid #000',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 140
              }}
              onClick={() => navigate(`/event-register/${event.id}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
              }}
            >
              {/* Content Section */}
              <div>
                {/* Event Title */}
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#1a1a1a',
                  marginBottom: 12,
                  lineHeight: '1.2'
                }}>
                  {event.title}
                </div>

                {/* Date and Location */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}>
                  <div style={{
                    fontSize: 10,
                    color: '#666',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    <span style={{ fontSize: 10 }}>📅</span>
                    {new Date(event.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>

                  {event.location && (
                    <div style={{
                      fontSize: 10,
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <span style={{ fontSize: 12 }}>📍</span>
                      {event.location}
                    </div>
                  )}
                </div>
              </div>

              {/* Register Button */}
              <button
                style={{
                  background: "#232323",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontWeight: 600,
                  fontSize: 12,
                  boxShadow: "0 2px 8px rgba(30,20,60,0.09)",
                  cursor: "pointer",
                  transition: 'background 0.16s',
                  width: '100%',
                  marginTop: 16
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#1a1a1a';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#232323';
                }}
              >
                REGISTER
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* E-Catalogue Download Section */}
      <div style={{
        marginTop: 12,
        marginBottom: 24,
        paddingLeft: 2,
        paddingRight: 2
      }}>
        {/* Header - Same as Collections */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10
        }}>
          <div style={{
            fontSize: 20,
            fontWeight: 600,
            color: '#233045'
          }}>E-Catalogue</div>
          <div style={{
            fontSize: 15,
            color: '#233045',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7,10 12,15 17,10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download PDFs
          </div>
        </div>

        {/* Catalogue Grid - 3 columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
          paddingBottom: 4
        }}>
          {catalogues.map((item) => (
            <div
              key={item.id}
              onClick={() => handleDownload(item.pdf_path || item.file, `${item.name}.pdf`)}
              style={{
                background: '#fff',
                borderRadius: 8,
                padding: 12,
                cursor: 'pointer',
                transition: 'box-shadow 0.15s',
                boxShadow: '0 0 0 rgba(0,0,0,0.05)',
                border: '1px solid #f0f0f0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: 100
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 0 rgba(0,0,0,0.05)';
              }}
            >
              {/* Image/Icon Container */}
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 6,
                background: item.image_path ? '#fff' : '#fff5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 8,
                border: '1px solid #e5e5e5',
                overflow: 'hidden'
              }}>
                {item.image_path ? (
                  <img
                    src={item.image_path.startsWith('http') ? item.image_path : `${API_BASE}${item.image_path}`}
                    alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10,9 9,9 8,9" />
                  </svg>
                )}
              </div>

              {/* Title */}
              <div style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#233045',
                marginBottom: 3,
                lineHeight: '1.2'
              }}>
                {item.name}
              </div>

              {/* File Info */}
              <div style={{
                fontSize: 11,
                color: '#666',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 3
              }}>
                <span style={{
                  padding: '1px 4px',
                  background: '#fee2e2',
                  color: '#dc2626',
                  borderRadius: 3,
                  fontSize: 9,
                  fontWeight: 600
                }}>
                  PDF
                </span>
                {item.size && (
                  <>
                    <span>•</span>
                    <span>{item.size}</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>


      </div>

      {/* Venzo Card */}
      <div style={{
        margin: '0 auto 20px auto',
        maxWidth: 520,
        borderRadius: 20,
        background: '#f5f5f5',
        boxShadow: "0 2px 16px 0 rgba(25,30,40,0.06)",
        padding: "18px 18px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 78,
        gap: 18
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={venzoLogo}
            alt="Venzo Logo"
            style={{
              width: 46,
              height: 46,
              objectFit: "contain",
              borderRadius: 14,
              background: "#f3f3f3",
              marginRight: 18,
              boxShadow: '0 1px 8px 0 rgba(0,0,0,0.05)'
            }}
          />
          <div style={{ fontWeight: 700, fontSize: 19, color: "#232323", letterSpacing: 0.5 }}>
            Venzowood
          </div>
        </div>
        <button
          onClick={() => {
            navigator.clipboard.writeText('https://venzowood.com');
            showSuccess("Link copied successfully");
          }}
          style={{
            background: "#232323",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "6px 12px",
            fontWeight: 600,
            fontSize: 12,
            boxShadow: "0 2px 8px rgba(30,20,60,0.09)",
            cursor: "pointer",
            transition: 'background 0.16s'
          }}
        >
          Copy Link
        </button>
      </div>

      {/* Disclaimer Marquee */}
      <div className="marquee-wrapper">
        <div className="marquee-content">
          Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary
        </div>
      </div>

      <BottomNav />



      {/* Story Popup - Instagram Style */}
      {popupStory && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: '#000', zIndex: 9999,
            display: 'flex', flexDirection: 'column'
          }}
        >
          {/* Progress Bar */}
          <div style={{ padding: '8px 6px', display: 'flex', gap: 4, position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10 }}>
            <div style={{ flex: 1, height: 2, background: 'rgba(255,255,255,0.3)', borderRadius: 2 }}>
              <div className="story-progress-fill" style={{ width: '0%', height: '100%', background: '#fff', borderRadius: 2 }}></div>
            </div>
          </div>

          {/* Header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '16px 12px 0 12px', color: '#fff',
            position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#333', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
                <img
                  src={user.avatar || DEFAULT_AVATAR}
                  alt="User"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <span style={{ fontWeight: 600, fontSize: 14, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                {popupStory.title || 'Story'}
              </span>
              <span style={{ fontSize: 12, opacity: 0.7, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>1h</span>
            </div>
            <button
              onClick={() => setPopupStory(null)}
              style={{
                background: 'none', border: 'none', color: '#fff',
                fontSize: 28, cursor: 'pointer', padding: 4, lineHeight: 0.5,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)'
              }}
            >
              ×
            </button>
          </div>

          {/* Main Content Area */}
          <div
            onClick={(e) => {
              const clickX = e.clientX;
              const screenWidth = window.innerWidth;
              const currentIndex = stories.findIndex(s => s.id === popupStory.id);

              if (clickX > screenWidth / 2) {
                // Right click -> Next
                if (currentIndex !== -1 && currentIndex < stories.length - 1) {
                  setPopupStory(stories[currentIndex + 1]);
                } else {
                  setPopupStory(null);
                }
              } else {
                // Left click -> Previous
                if (currentIndex > 0) {
                  setPopupStory(stories[currentIndex - 1]);
                }
              }
            }}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              height: '100%',
              background: '#111'
            }}
          >
            <img
              src={
                popupStory.image_path?.startsWith('http')
                  ? popupStory.image_path
                  : `${API_BASE}${popupStory.image_path}`
              }
              alt={popupStory.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block'
              }}
            />

            {/* Bottom Gradient for better text visibility */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, width: '100%', height: '20%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
              pointerEvents: 'none'
            }}></div>

            {/* Caption - Bottom Left */}
            <div style={{
              position: 'absolute', bottom: 30, left: 20,
              color: '#fff', maxWidth: '75%', zIndex: 5
            }}>
              <div style={{
                fontSize: 15, fontWeight: 500, lineHeight: 1.4,
                textShadow: '0 1px 2px rgba(0,0,0,0.8)'
              }}>
                {popupStory.description || popupStory.title}
              </div>
            </div>

            {/* Like Button - Bottom Right */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                // Add like animation logic here if needed
              }}
              style={{
                position: 'absolute', bottom: 25, right: 20,
                cursor: 'pointer', zIndex: 10,
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
